import { computed, makeObservable } from 'mobx'

import i18n from 'modules/core/lib/i18n'
import { Model } from 'modules/core/lib/mobx-rest'

export const EMPLOYEE_HIRED_DATE = 'employee_hired_date'

// TODO: delete this timeoff_cycle when all flexible cycles are working
export const TIMEOFF_CYCLE = [
  'jan_dec',
  'feb_jan',
  'mar_feb',
  'apr_mar',
  'may_apr',
  'jun_may',
  'jul_jun',
  'aug_jul',
  'sep_aug',
  'oct_sep',
  'nov_oct',
  'dec_nov',
  EMPLOYEE_HIRED_DATE,
]

export const DEFAULT_CYCLE_LENGTH = 12

export const DEFAULT_TIMEOFF_CYCLE = TIMEOFF_CYCLE[0]

enum FREQUENCIES {
  yearly = 'yearly',
  monthly_flexible = 'monthly_flexible',
  lifetime = 'lifetime',
}

enum DAYS_TYPE {
  working_days = 'working_days',
  natural_days = 'natural_days',
  french_calendar_days = 'french_calendar_days',
  french_ouvres = 'french_ouvres',
}

enum RANGE_OPTIONS {
  exact_range = 'exact_range',
  extra_non_working_days_at_end = 'extra_non_working_days_at_end',
}

enum PRORATION_TYPE {
  proration_enabled = 'proration_enabled',
  proration_disabled = 'proration_disabled',
}

enum NEGATIVE_COUNTER_TYPE {
  negative_counter_enabled = 'negative_counter_enabled',
  negative_counter_disabled = 'negative_counter_disabled',
}

// TODO: remove this enum when allowance is migrated to graphql
// eslint-disable-next-line factorial/no-unhealthy-enums
enum LIMIT_NEGATIVE_COUNTER_TYPE {
  no_limit = 'no_limit',
  limit_negative_counter = 'limit_negative_counter',
}

enum AVAILABLE_OPTIONS_DAYS {
  all_days = 'all_days',
  generated_days = 'generated_days',
  generated_days_monthly = 'generated_days_monthly',
}

enum ROUNDING_OPTIONS_DAYS {
  half_day = 'half_day',
  decimals = 'decimals',
  round_up = 'round_up',
}

enum ROUNDING_OPTIONS_HOURS {
  decimals = 'decimals',
  quarters = 'quarters',
  half_day = 'half_day',
}

enum ALLOWANCE_TYPES {
  days = 'days',
  hours = 'hours',
}

enum CARRY_OVER_STRATEGIES {
  unlimited = 'unlimited',
  one_units = 'one_units',
  zero_units = 'zero_units',
  several_units = 'several_units',
}

enum TENURE_PERIOD_TRANSITION {
  beginning_of_cycle = 'beginning_of_cycle',
  end_of_cycle = 'end_of_cycle',
  after_milestone = 'after_milestone',
}

enum ACCRUED_UNITS_AVAILABILITY {
  current_cycle = 'current_cycle',
  next_cycle = 'next_cycle',
}

enum SOURCE_UNITS {
  base_units = 'base_units',
  overtime_units = 'overtime_units',
  by_worked_time = 'by_worked_time', // AKA: accrued paid timeoff
}

enum CYCLE_START {
  jan = 'jan',
  feb = 'feb',
  mar = 'mar',
  apr = 'apr',
  may = 'may',
  jun = 'jun',
  jul = 'jul',
  aug = 'aug',
  sep = 'sep',
  oct = 'oct',
  nov = 'nov',
  dec = 'dec',
  employee_hired_date = 'employee_hired_date',
}

// TODO: In the future we might change this selector for a input field,
// but meantime we will give the employee some combination of months.
export const CYCLE_DURATION = [1, 2, 3, 4, 6, 12]

interface IAllowance {
  [key: string]: any
}

class Allowance extends Model<IAllowance> {
  static FREQUENCIES = FREQUENCIES

  static DAYS_TYPE = DAYS_TYPE

  static RANGE_OPTIONS = RANGE_OPTIONS

  static PRORATION_TYPE = PRORATION_TYPE

  static NEGATIVE_COUNTER_TYPE = NEGATIVE_COUNTER_TYPE
  static LIMIT_NEGATIVE_COUNTER_TYPE = LIMIT_NEGATIVE_COUNTER_TYPE

  static AVAILABLE_OPTIONS_DAYS = AVAILABLE_OPTIONS_DAYS

  static DEFAULT_ROUNDING_OPTION_HOURS = 'half_day'

  static DEFAULT_ROUNDING_OPTION_DAYS = 'half_day'

  static ROUNDING_OPTIONS_DAYS = ROUNDING_OPTIONS_DAYS

  static ROUNDING_OPTIONS_HOURS = ROUNDING_OPTIONS_HOURS

  static ALLOWANCE_TYPES = ALLOWANCE_TYPES

  static CARRY_OVER_STRATEGIES = CARRY_OVER_STRATEGIES

  static TENURE_PERIOD_TRANSITION = TENURE_PERIOD_TRANSITION

  static ACCRUED_UNITS_AVAILABILITY = ACCRUED_UNITS_AVAILABILITY

  static SOURCE_UNITS = SOURCE_UNITS

  static CYCLE_START = CYCLE_START

  static FRANCE_WITH_DEPARTMENTS: { [key: string]: string } = {
    fr: 'france',
    yt: 'mayotte',
    re: 'reunion',
    gf: 'guiana',
    mq: 'martinique',
    gp: 'guadeloupe',
    pf: 'la polynesie française',
  }

  constructor(attr: IAllowance = {}) {
    super(attr)
    makeObservable(this, {
      holidayAllowance: computed,
      isRoundUp: computed,
      isDecimal: computed,
      isDaysAndHalfDays: computed,
      natural: computed,
      workingDays: computed,
      regularCalendarDays: computed,
      frenchCalendarDays: computed,
      frenchOuvre: computed,
      generatedDays: computed,
      subtitle: computed,
      isEmployeeCycle: computed,
      isHourly: computed,
      isDays: computed,
      carryOverStrategy: computed,
      neverExpires: computed,
      isNextCycleAvailability: computed,
      isOvertime: computed,
      isTimeWorked: computed,
      maximumAmount: computed,
    })
  }

  static urlRoot() {
    return '/allowances'
  }

  get indexes() {
    return ['timeoff_policy_id']
  }

  get holidayAllowance(): number {
    return Number(this.attributes.holiday_allowance_in_cents / 100)
  }

  get maximumAmount(): number {
    return Number(this.attributes.maximum_amount_in_cents / 100)
  }

  get natural(): boolean {
    return this.attributes.days_type === Allowance.DAYS_TYPE.natural_days
  }

  get isRoundUp(): boolean {
    return this.attributes.rounding === Allowance.ROUNDING_OPTIONS_DAYS.round_up
  }

  get isDaysAndHalfDays(): boolean {
    return this.attributes.rounding === Allowance.ROUNDING_OPTIONS_DAYS.half_day
  }

  get isDecimal(): boolean {
    return this.attributes.rounding === Allowance.ROUNDING_OPTIONS_DAYS.decimals
  }

  get isHourHalfDays(): boolean {
    return this.attributes.rounding === Allowance.ROUNDING_OPTIONS_HOURS.half_day
  }

  get isHourQuarters(): boolean {
    return this.attributes.rounding === Allowance.ROUNDING_OPTIONS_HOURS.quarters
  }

  get workingDays(): boolean {
    return this.attributes.days_type === Allowance.DAYS_TYPE.working_days
  }

  get frenchCalendarDays(): boolean {
    return this.attributes.days_type === Allowance.DAYS_TYPE.french_calendar_days
  }

  get countSmRestDays(): boolean {
    return !!this.attributes.count_sm_rest_days
  }

  get frenchOuvre(): boolean {
    return this.attributes.days_type === Allowance.DAYS_TYPE.french_ouvres
  }

  get regularCalendarDays(): boolean {
    return this.attributes.range_type === Allowance.RANGE_OPTIONS.extra_non_working_days_at_end
  }

  get generatedDays(): boolean {
    return this.attributes.available_days === 'generated_days'
  }

  get subtitle(): string {
    if (this.attributes.unlimited_holidays) {
      switch (this.attributes.allowance_type) {
        case Allowance.ALLOWANCE_TYPES.hours:
          return i18n.t('settings.timeoff_allowances.unlimited_holidays_hours')

        case Allowance.ALLOWANCE_TYPES.days:
        default:
          return i18n.t('settings.timeoff_allowances.unlimited_holidays_days')
      }
    } else if (this.natural) {
      switch (this.attributes.allowance_type) {
        case Allowance.ALLOWANCE_TYPES.hours:
          return i18n.tp('settings.timeoff_allowances.count_hours', {
            count: this.holidayAllowance,
          })

        case Allowance.ALLOWANCE_TYPES.days:
        default:
          return i18n.tp('settings.timeoff_allowances.count_days', {
            count: this.holidayAllowance,
          })
      }
    } else if (this.frenchCalendarDays) {
      return i18n.tp('settings.timeoff_allowances.count_ouvrables_days', {
        count: this.holidayAllowance,
      })
    } else if (this.isTimeWorked) {
      return i18n.t('settings.timeoff_allowances.by_worked_hours')
    } else if (this.isOvertime) {
      return i18n.t('settings.timeoff_allowances.overtime_compensation')
    } else {
      switch (this.attributes.allowance_type) {
        case Allowance.ALLOWANCE_TYPES.hours:
          return i18n.tp('settings.timeoff_allowances.count_working_hours', {
            count: this.holidayAllowance,
          })

        case Allowance.ALLOWANCE_TYPES.days:
        default:
          return i18n.tp('settings.timeoff_allowances.count_working_days', {
            count: this.holidayAllowance,
          })
      }
    }
  }

  get isEmployeeCycle(): boolean {
    return this.attributes.timeoff_cycle === Allowance.CYCLE_START.employee_hired_date
  }

  get hasProrationEnabled(): boolean {
    return this.attributes.proration_type === Allowance.PRORATION_TYPE.proration_enabled
  }

  get isHourly(): boolean {
    return this.attributes.allowance_type === Allowance.ALLOWANCE_TYPES.hours
  }

  get isDays(): boolean {
    return this.attributes.allowance_type === Allowance.ALLOWANCE_TYPES.days
  }

  get isOvertime(): boolean {
    return this.attributes.source_units === Allowance.SOURCE_UNITS.overtime_units
  }

  get isTimeWorked(): boolean {
    // AKA: accrued paid timeoff
    return this.attributes.source_units === Allowance.SOURCE_UNITS.by_worked_time
  }

  get isLifetime(): boolean {
    return this.attributes.frequency === Allowance.FREQUENCIES.lifetime
  }

  get carryOverStrategy(): string {
    if (this.attributes.unlimited_carry_over) {
      return Allowance.CARRY_OVER_STRATEGIES.unlimited
    }

    if (this.attributes.carry_over_units_in_cents === 0) {
      return Allowance.CARRY_OVER_STRATEGIES.zero_units
    }

    if (this.attributes.carry_over_units_in_cents === 100) {
      return Allowance.CARRY_OVER_STRATEGIES.one_units
    }

    return Allowance.CARRY_OVER_STRATEGIES.several_units
  }

  get neverExpires(): boolean {
    return this.attributes.unlimited_carry_over_expiration === true
  }

  get expiresInRoundYears(): boolean {
    return +this.attributes.expire_in_months % 12 === 0
  }

  get expireInYears(): number {
    return +this.attributes.expire_in_months / 12
  }

  get expireInMonths(): number {
    return +this.attributes.expire_in_months
  }

  get carryOverUnits(): number {
    return +(this.attributes.carry_over_units_in_cents / 100)
  }

  accruedUnitsAvailabilityOptions(option: string): string {
    switch (option) {
      case Allowance.ACCRUED_UNITS_AVAILABILITY.current_cycle:
        return i18n.t('time_off.allowances.fields.accrued_units_availability_options.current_cycle')

      case Allowance.ACCRUED_UNITS_AVAILABILITY.next_cycle:
        return i18n.t('time_off.allowances.fields.accrued_units_availability_options.next_cycle')

      default:
        return ''
    }
  }

  get isNextCycleAvailability(): boolean {
    return (
      this.attributes.accrued_units_availability === Allowance.ACCRUED_UNITS_AVAILABILITY.next_cycle
    )
  }
}

export default Allowance
